import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  StepAction,
  StepBehaviour,
  ConditionalBehaviour,
  OptionsData,
} from '../../models';
import { SegmentDto } from 'workflow-definition/openapi';

export enum RightSidebars {
  ACTION_PROPERTIES = 'ACTION_PROPERTIES',
  STEP_PROPERTIES = 'STEP_PROPERTIES',
  SEGMENT_PROPERTIES = 'SEGMENT_PROPERTIES',
}

export enum LeftSidebars {
  SEGMENTS_LIST = 'SEGMENTS_LIST',
}

@Injectable({
  providedIn: 'root',
})
export class CurrentWorkflowService {
  private _componentActiveTabIndex = signal(0);

  private segmentOverrides = new Map<number, Partial<SegmentDto>>();

  setSegmentOverride(segmentId: number, updates: Partial<SegmentDto>): void {
    this.segmentOverrides.set(segmentId, {
      ...(this.segmentOverrides.get(segmentId) ?? {}),
      ...updates,
    });
  }

  getMergedSegment(seg: SegmentDto): SegmentDto {
    if (!seg.id) return seg;
    return { ...seg, ...(this.segmentOverrides.get(seg.id) ?? {}) };
  }

  private _currentSegmentId = signal<number>(-1);
  private _currentStepId = signal<number>(-1);
  private _currentActionId = signal<number>(-1);
  private _currentDivision = signal<string>('');
  private _isPreviewMode = signal<boolean>(false);

  private _segmentPropertiesSidebarVisible = signal(false);
  private _stepPropertiesSidebarVisible = signal(false);
  private _actionPropertiesSidebarVisible = signal(false);

  private _stepActionToCreate = signal<StepAction | null>(null);

  private message = signal('');

  public isCreateWorkflowActionHit: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public isWorkflowBuilderSelected: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public isWorkflowBuilderClosed: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public isConditionalsSaved: BehaviorSubject<
    ConditionalBehaviour | undefined
  > = new BehaviorSubject<ConditionalBehaviour | undefined>(undefined);

  public isStepSaved: BehaviorSubject<StepBehaviour | undefined> =
    new BehaviorSubject<StepBehaviour | undefined>(undefined);

  public shouldRefreshFlowNodes: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public canvasStepAction = new Subject<{
    optionData: OptionsData<string> | undefined;
    id: number;
    name?: string;
  }>();

  public currentDivision = new BehaviorSubject<string>('');

  setPreviewMode(val: boolean) {
    this._isPreviewMode.set(val);
  }

  get isPreviewMode() {
    return this._isPreviewMode();
  }

  setCurrentDivision(val: string) {
    if (val === this.currentDivision.value) return;
    this.currentDivision.next(val);
  }

  public currentSegmentName = new BehaviorSubject<string>('');
  public currentSegmentOrder = new BehaviorSubject<number>(1);
  public currentSegments = new BehaviorSubject<SegmentDto[]>([]);

  setComponentActiveTabIndex(val: number | null) {
    this._componentActiveTabIndex.set(val || 0);
  }

  get componentActiveTabIndex() {
    return this._componentActiveTabIndex();
  }

  /**
   * Renders a specific sidebar in the right sidebar, possible options included in the
   * RightSidebars enum.
   * @param sidebar Option part of the RightSidebars enum
   */

  renderRightSidebar(sidebar: RightSidebars | null | undefined) {
    switch (sidebar) {
      case RightSidebars.ACTION_PROPERTIES:
        this.setActionPropertiesSidebarVisible(true);
        this.setStepPropertiesSidebarVisible(false);
        this.setSegmentPropertiesSidebarVisible(false);
        break;
      case RightSidebars.STEP_PROPERTIES:
        this.setActionPropertiesSidebarVisible(false);
        this.setStepPropertiesSidebarVisible(true);
        this.setSegmentPropertiesSidebarVisible(false);
        break;
      case RightSidebars.SEGMENT_PROPERTIES:
        this.setActionPropertiesSidebarVisible(false);
        this.setStepPropertiesSidebarVisible(false);
        this.setSegmentPropertiesSidebarVisible(true);
        break;
      case null:
      case undefined:
      default:
        this.setActionPropertiesSidebarVisible(false);
        this.setStepPropertiesSidebarVisible(false);
        this.setSegmentPropertiesSidebarVisible(false);
        break;
    }
  }

  setCurrentSegmentId(val: number | null) {
    this._currentSegmentId.set(val || -1);
  }

  get currentSegmentId() {
    return this._currentSegmentId();
  }

  setCurrentStepId(val: number | null) {
    this._currentStepId.set(val || -1);
  }

  get currentStepId() {
    return this._currentStepId();
  }

  setCurrentActionId(val: number | null) {
    this._currentActionId.set(val || -1);
  }

  get currentActionId() {
    return this._currentActionId();
  }

  setSegmentPropertiesSidebarVisible(val: boolean) {
    this._segmentPropertiesSidebarVisible.set(val);
  }

  get segmentPropertiesSidebarVisible() {
    return this._segmentPropertiesSidebarVisible();
  }

  setStepActionToCreateSignal(val: StepAction | null) {
    this._stepActionToCreate.set(val);
  }

  get stepActionToCreateSignal() {
    return this._stepActionToCreate();
  }

  setStepPropertiesSidebarVisible(val: boolean) {
    this._stepPropertiesSidebarVisible.set(val);
  }

  get stepPropertiesSidebarVisible() {
    return this._stepPropertiesSidebarVisible();
  }

  setActionPropertiesSidebarVisible(val: boolean) {
    this._actionPropertiesSidebarVisible.set(val);
  }

  get actionPropertiesSidebarVisible() {
    return this._actionPropertiesSidebarVisible();
  }

  setMessageSignal(val: string) {
    this.message.set(val);
  }

  get messageSignal() {
    return this.message();
  }
}
